import * as ko from 'knockout';
import * as analytics from 'analytics';
import { identity, memoize, sortBy } from 'lodash-es';

import { ApiService } from '@/features/api';
import eventsService, { SubscribedEvent } from '@/legacy/services/eventsService';

import { API_PREFIX } from '../api/constants';
import NetSuiteLogo from '../assets/netsuite-logo.svg';
import AccountingSoftwareCompany from '../types/AccountingSoftwareCompany';
import AccountingSoftwareContact from '../types/AccountingSoftwareContact';
import Bill from '../types/Bill';
import BillData from '../types/BillData';
import CodatConnection from '../types/CodatConnection';
import CodatEvents from '../types/CodatEvents';
import { CodatDataType, CodatSyncMessage } from '../types/CodatSync';
import IAccount from '../types/IAccount';
import IAccountingSoftwareAddAccount from '../types/IAccountingSoftwareAddAccount';
import IBillAttachmentDetails from '../types/IBillAttachmentDetails';
import Invoice from '../types/Invoice';
import InvoiceData from '../types/InvoiceData';
import { SupportedAccountingSoftware } from '../types/SupportedAccountingSoftware';
import SyncCodatDataResponse from '../types/SyncCodatDataResponse';
import SyncType from '../types/SyncType';
import setupPopupWindow from '../utils/setupPopupWindow';

import BaseAccountingSoftwarePackage from './BaseAccountingSoftwarePackage';

const syncTimeoutError = new Error('Timed out waiting for NetSuite data sync');

/** This is a special sync state for the global state of this package. */
enum SyncState {
  None,
  Pending,
  Completed,
}

class NetSuiteAccountingPackage extends BaseAccountingSoftwarePackage {
  private readonly connectIcon = 'third_party_netsuite_connect';

  // TODO: remove vendor logo from public dir once we fully migrate to the new UI
  readonly logoUrl = `${import.meta.env.APP_STATIC_APP_ROOT_URL}/img/vendor/netsuite-icon.svg`;

  readonly icon = NetSuiteLogo;

  private readonly maxWaitTimeForDataSyncInMilliseconds = 1000 * 60 * 10;

  supportsInvoices = true;

  usesCodat = true;

  private syncState: KnockoutObservable<Map<CodatDataType, SyncState>> = ko.observable(
    new Map(Object.values(CodatDataType).map((dataType) => [dataType, SyncState.None]))
  );

  getSyncProgress = memoize(
    (syncDataTypes: ReadonlyArray<CodatDataType>) =>
      ko.pureComputed(() => {
        if (syncDataTypes == null) {
          return undefined;
        }
        const syncState = this.syncState();
        const completed = syncDataTypes.filter(
          (dataType) => syncState.get(dataType) === SyncState.Completed
        );
        return (100 * completed.length) / syncDataTypes.length;
      }),
    (syncDataTypes) => String(sortBy(syncDataTypes, identity))
  );

  constructor() {
    super(SupportedAccountingSoftware.NetSuite);
  }

  /**
   * Get NetSuite integration accounts from Codat.
   *
   * In order to behave like other packages, this method has to wait for synced data signals
   * before fetching the accounts from the associated API endpoint; otherwise, it will return an
   * empty response.
   */
  async GetAccounts(): Promise<Array<IAccount>> {
    const companyId: string = this.companyService.CompanyId();

    const url = new URL(
      `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/codat/accounts`
    );
    const accounts = await ApiService.get<Array<IAccount>>(url.toString());
    return accounts;
  }

  async AddAccount(account: IAccountingSoftwareAddAccount): Promise<IAccount> {
    const companyId: string = this.companyService.CompanyId();

    const url = new URL(
      `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/codat/accounts`
    );

    const response = await ApiService.post<IAccount>(url.toString(), { body: account });

    return response;
  }

  async Connect(): Promise<any> {
    const companyId: string = this.companyService.CompanyId();

    const codatConnection = await ApiService.post<CodatConnection>(
      `${import.meta.env.APP_URLS_API_URL}/v1/companies/${companyId}/codat/connections`,
      undefined,
      true
    );

    const popup = setupPopupWindow();
    if (popup) {
      popup.location.href = codatConnection.connectionLink;
    }
  }

  ConnectConnectIcon(): string {
    return this.connectIcon;
  }

  async Connected(info: any): Promise<void> {
    console.log('connected');
  }

  FilterResolutions(conflictingAccountName: string): string {
    return conflictingAccountName;
  }

  GenerateConnectButton($target: JQuery): void {
    $('<a/>', {
      href: '#',
      class: this.connectIcon,
    })
      .appendTo($target)
      .click(() => {
        this.Connect();
        return false;
      });
  }

  /**
   * Get NetSuite bills from Codat.
   *
   * In order to behave like other packages, this method has to wait for synced data signals
   * before fetching the bills from the associated API endpoint; otherwise, it will return an
   * empty response.
   */
  async GetBills(): Promise<Array<Bill>> {
    await this.waitForSyncedDataTypes([CodatDataType.Bills]);

    const companyId: string = this.companyService.CompanyId();

    const billData = await ApiService.get<Array<BillData>>(
      `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/codat/bills`
    );

    if (companyId !== this.companyService.CompanyId()) {
      throw new Error('Company switched while fetching bills');
    }

    return billData.map((data) => new Bill(data));
  }

  /** Get company associated with NetSuite integration from Codat. */
  async fetchCompany(): Promise<AccountingSoftwareCompany> {
    const companyId: string = this.companyService.CompanyId();
    const codatConnection = await ApiService.get<CodatConnection>(
      `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/codat/connections`,
      undefined,
      true
    );
    return { name: codatConnection?.codatCompanyName };
  }

  async GetContacts(): Promise<Array<AccountingSoftwareContact>> {
    return [];
  }

  /**
   * Get NetSuite invoices from Codat.
   *
   * In order to behave like other packages, this method has to wait for synced data signals
   * before fetching the invoices from the associated API endpoint; otherwise, it will return an
   * empty response.
   */
  async GetInvoices(): Promise<Array<Invoice>> {
    await this.waitForSyncedDataTypes([CodatDataType.Invoices]);

    const companyId: string = this.companyService.CompanyId();

    const invoiceData = await ApiService.get<Array<InvoiceData>>(
      `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/codat/invoices`
    );

    if (companyId !== this.companyService.CompanyId()) {
      throw new Error('Company switched while fetching invoices');
    }

    return invoiceData.map((data) => new Invoice(data));
  }

  async GetReconciliationStatus(): Promise<string> {
    return 'Reconciled';
  }

  GetBillAttachment(attachment: IBillAttachmentDetails) {
    const popup = setupPopupWindow({ width: 800, height: 650 });
    if (popup) {
      popup.location.href = attachment.url;
    }
  }

  private async waitForSyncedDataTypes(dataTypes: ReadonlyArray<CodatDataType>): Promise<void> {
    await this.syncState.skipUntil((syncState) =>
      dataTypes.every((dataType) => syncState.get(dataType) === SyncState.Completed)
    );
  }

  private updateSyncState(dataTypes: ReadonlyArray<CodatDataType>, state: SyncState) {
    const next = new Map(this.syncState());
    dataTypes.forEach((dataType) => {
      next.set(dataType, state);
    });
    this.syncState(next);
  }

  private dataSyncSubscription: SubscribedEvent;

  private startSyncListener() {
    if (this.dataSyncSubscription != null) {
      return;
    }

    let lastTimeDataTypeWasSynced: number = Date.now();
    const syncStartTime: number = Date.now();

    // This event is triggered by the RealtimeService when Codat has finished syncing data
    this.dataSyncSubscription = eventsService.on(
      CodatEvents.DataSync,
      ({ detail: { dataTypes } }: CustomEvent<CodatSyncMessage>) => {
        this.updateSyncState(dataTypes, SyncState.Completed);

        const now = Date.now();
        const elapsedTime = now - lastTimeDataTypeWasSynced;
        const elapsedTimeInSeconds = (elapsedTime / 1000).toFixed(2);
        const totalTime = now - syncStartTime;
        const totalTimeInSeconds = Math.ceil(totalTime / 1000);

        dataTypes.forEach((dataType) => {
          // Intentional logging.
          // eslint-disable-next-line no-console
          console.log(`[NetSuite] synced ${dataType} data type.`);
        });
        // Intentional logging.
        // eslint-disable-next-line no-console
        console.log(
          `[NetSuite] Synced data types in ${elapsedTimeInSeconds}s (total time: ${totalTimeInSeconds}s)`
        );

        lastTimeDataTypeWasSynced = Date.now();

        this.maybeStopSyncListener();
      }
    );
  }

  private stopSyncListener() {
    if (this.dataSyncSubscription == null) {
      return;
    }

    eventsService.off(this.dataSyncSubscription);
    this.dataSyncSubscription = null;
  }

  private maybeStopSyncListener() {
    if (this.dataSyncSubscription == null) {
      return;
    }

    const hasPendingStates = Array.from(this.syncState().values()).some(
      (state) => state === SyncState.Pending
    );
    if (hasPendingStates) {
      return;
    }

    this.stopSyncListener();
  }

  /**
   * Trigger the data synchronization process
   *
   * This method kicks off the data synchronization process with Codat, then awaits
   * data sync signals from the RealtimeService before resolving.
   */
  SyncData(syncType: SyncType, syncDataTypes: ReadonlyArray<CodatDataType>): Promise<void> {
    this.fireAccountingSoftwareConnectingEvent();
    const companyId: string = this.companyService.CompanyId();

    this.updateSyncState(syncDataTypes, SyncState.Pending);
    this.startSyncListener();

    const syncPromise = ApiService.post<SyncCodatDataResponse>(
      `${import.meta.env.APP_URLS_API_URL}/v1/companies/${companyId}/codat/sync`,
      {
        body: { syncType, syncDataTypes },
      }
    )
      .then(async (response) => {
        // When isSyncRequestSent is true, the backend will sync ALL data types, which are already
        // in the pending state.Otherwise, the backend will sync NONE of the data types, which we
        // should move to the completed state ourselves.
        if (response.isSyncRequestSent) {
          syncDataTypes?.forEach((dataType) => {
            // Intentional logging.
            // eslint-disable-next-line no-console
            console.log(`[NetSuite] requesting sync for ${dataType}`);
          });
          await this.waitForSyncedDataTypes(syncDataTypes);
        } else {
          this.updateSyncState(syncDataTypes, SyncState.Completed);
        }

        this.fireAccountingSoftwareConnectedEvent();
        analytics.send('event', 'Company-AccountingSoftware', 'Connect', this.shortName);
      })
      .finally(() => {
        this.maybeStopSyncListener();
      });
    const timeoutPromise = new Promise<void>((_resolve, reject) => {
      setTimeout(reject, this.maxWaitTimeForDataSyncInMilliseconds, syncTimeoutError);
    });

    return Promise.race([syncPromise, timeoutPromise]);
  }
}

export type { CodatSyncMessage as NetSuiteSyncMessage };
export default NetSuiteAccountingPackage;
